import React, { useMemo } from 'react';
import PaginationTable, { PaginationTableProps } from 'ui/PaginationTable/PaginationTable';
import { TiktokPostInviteData } from '../useTiktokPostInvites';
import { Column } from 'react-table';
import styles from './PostedTable.module.css';
import { Button, Skeleton } from '@round/ui-kit';
import TiktokUserBadge from 'Modules/TikTok/components/TiktokUserBadge/TiktokUserBadge';
import { ReactComponent as LinkIcon } from 'assets/icons/Link.svg';
import { ReactComponent as TimerIcon } from 'assets/Timer.svg';

type Props = Omit<PaginationTableProps<TiktokPostInviteData>, 'columns' | 'loading'> & {
    isLoading: boolean;
    onEmailHistoryClicked: (row: TiktokPostInviteData) => void;
};

const formatDateTime = (datetime: string) =>
    new Date(datetime).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
    });

const PostedTable = ({ onEmailHistoryClicked, ...props }: Props) => {
    const columns: Column<TiktokPostInviteData>[] = useMemo(
        () => [
            {
                Header: 'Account',
                accessor: 'tiktokUserId',
                disableSortBy: true,
                Cell: ({
                    row: {
                        original: { userData },
                    },
                }) => <TiktokUserBadge isLoading={props.isLoading} data={userData} />,
            },
            {
                Header: 'Post',
                id: 'post_create_time',
                accessor: 'postCreateTime',
                Cell: ({ value: postCreateTime, row }) => {
                    if (props.isLoading) {
                        return <Skeleton />;
                    }

                    if (!postCreateTime || !row.original.postUrl) {
                        return <>-</>;
                    }

                    const postUrl = row.original.postUrl;
                    const postCreateTimeDateString = formatDateTime(postCreateTime);

                    return (
                        <div className={styles.postStatus}>
                            <span>{postCreateTimeDateString}</span>
                            <a href={postUrl} target="_blank" rel="noreferrer">
                                <LinkIcon className={styles.linkIcon} />
                            </a>
                        </div>
                    );
                },
            },
            {
                Header: 'Audio',
                accessor: 'audioId',
                disableSortBy: true,
                Cell: ({
                    row: {
                        original: { audio },
                    },
                }) => {
                    if (props.isLoading) {
                        return <Skeleton />;
                    }

                    return <>{audio?.title}</>;
                },
            },
            {
                Header: 'Last contacted at',
                id: 'last_contacted_at',
                accessor: 'lastContactedAt',
                Cell: ({ value, row: { original } }) => {
                    if (props.isLoading) {
                        return <Skeleton />;
                    }

                    return (
                        <div className={styles.lastContactedAtContainer}>
                            {value ? formatDateTime(value) : '-'}
                            <Button className={styles.historyButton} onClick={() => onEmailHistoryClicked(original)}>
                                Email history
                                <TimerIcon className={styles.historyIcon} />
                            </Button>
                        </div>
                    );
                },
            },
        ],
        [onEmailHistoryClicked, props.isLoading]
    );

    return <PaginationTable columns={columns} {...props} loading={props.isLoading} />;
};

export default PostedTable;
